import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Fade from '@mui/material/Fade';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { hasPageAccess } from '../utils/rbac';
import { jiraApi, JiraProject, JiraIssue } from '../api/client';
import { usersApi } from '../api/client';
import { ppmProjectsApi, PpmProjectListItem, kanbanCardsApi, KanbanCard } from '../api/ppm';
import { useTenant } from '../contexts/TenantContext';
import { useTenantNav } from '../hooks/useTenantNav';
// Dashboard uses MUI-only styling now.
import { PageHeader } from './layout/PageHeader';
import { PageLoader } from './PageLoader';
import { tokens } from '../theme/tokens';

const StatCard: React.FC<{
  title: string;
  value: number;
  subtitle: string;
  accent: string;
  delayMs?: number;
  onClick?: () => void;
}> = React.memo(({ title, value, subtitle, accent, delayMs = 0, onClick }) => (
  <Fade in timeout={450} style={{ transitionDelay: `${delayMs}ms` }}>
    <Card
      variant="outlined"
      sx={{
        borderColor: tokens.color.border,
        background: '#fff',
        overflow: 'hidden',
        position: 'relative',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: accent,
        },
        ...(onClick
          ? { '&:hover': { borderColor: tokens.color.borderStrong, backgroundColor: tokens.color.surfaceMuted } }
          : {}),
      }}
    >
      <ButtonBase onClick={onClick} disabled={!onClick} sx={{ width: '100%', textAlign: 'left' }}>
        <CardContent sx={{ width: '100%' }}>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.6 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.25 }}>
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </CardContent>
      </ButtonBase>
    </Card>
  </Fade>
));

const Dashboard: React.FC = () => {
  const location = useLocation();
  const { navigate } = useTenantNav();
  const { dbUser, isLoading: authLoading, isAuthenticated } = useCurrentUser();
  const canViewAll = hasPageAccess(dbUser, 'dashboard');
  const { currentTenant } = useTenant();

  function fmtDate(d: Date) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [jiraIssues, setJiraIssues] = useState<JiraIssue[]>([]);

  const [ppmProjects, setPpmProjects] = useState<PpmProjectListItem[]>([]);
  const [ppmCards, setPpmCards] = useState<KanbanCard[]>([]);

  const [loading, setLoading] = useState(true);
  const [topPanel, setTopPanel] = useState<null | 'activeProjects' | 'openWork' | 'dueSoon'>(null);
  const [showAllPanel, setShowAllPanel] = useState(false);

  const lastFetchedSlug = useRef<string | null>(null);

  const fetchDashboardData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (canViewAll) {
        // Admin-only: user directory access (kept for future admin enhancements)
        try {
          await usersApi.getAll({ scopePage: 'overview' });
        } catch {
          // ignore
        }
      }

      if (canViewAll) {
        // Admin workspace: show cross-project Jira workstream
        const projectsResponse = await jiraApi.getProjects();
        if (projectsResponse.data.success) {
          const ps = projectsResponse.data.projects || [];
          setJiraProjects(ps);

          const issuesPromises = ps.map(async (project) => {
            try {
              const jql = `project = ${project.key}`;
              const issuesResponse = await jiraApi.searchIssues(jql, [
                'summary',
                'status',
                'assignee',
                'created',
                'updated',
                'duedate',
                'priority',
                'issuetype',
                'reporter',
              ]);
              return issuesResponse.data.success ? (issuesResponse.data.issues || []) : [];
            } catch {
              return [];
            }
          });
          const allIssuesArrays = await Promise.all(issuesPromises);
          setJiraIssues(allIssuesArrays.flat());
        }

        // Also include INTERNAL projects + tasks for admins
        try {
          const ppmRes = await ppmProjectsApi.getAll({ source: 'internal' });
          const internal = ppmRes.data || [];
          setPpmProjects(internal);

          const cardsArrays = await Promise.all(
            internal.map(async (p) => {
              try {
                const r = await kanbanCardsApi.getAll({ projectId: p.id });
                return r.data || [];
              } catch {
                return [];
              }
            }),
          );
          setPpmCards(cardsArrays.flat());
        } catch {
          // ignore
        }
      } else {
        // User workspace: backend already scopes /ppm/projects to projects assigned to this user
        const ppmRes = await ppmProjectsApi.getAll({ source: 'internal' });
        const mine = ppmRes.data || [];
        setPpmProjects(mine);

        const cardsArrays = await Promise.all(
          mine.map(async (p) => {
            try {
              const r = await kanbanCardsApi.getAll({ projectId: p.id });
              return r.data || [];
            } catch {
              return [];
            }
          }),
        );
        setPpmCards(cardsArrays.flat());
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (!currentTenant) return;
    // Skip if we already fetched for this exact tenant
    const slug = currentTenant.slug;
    if (lastFetchedSlug.current === slug) return;
    const silent = lastFetchedSlug.current !== null; // silent if switching tenants (not first load)
    lastFetchedSlug.current = slug;
    fetchDashboardData(silent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canViewAll, currentTenant?.slug]);

  const stats = useMemo(() => {
    if (canViewAll) {
      const activeProjects = jiraProjects.length + ppmProjects.length;
      const inProgress = jiraIssues.filter((i) => {
        const status = i.fields.status.name.toLowerCase();
        return status.includes('progress') || status.includes('development');
      }).length;
      const open = jiraIssues.filter((i) => {
        const status = i.fields.status.name.toLowerCase();
        return !status.includes('done') && !status.includes('closed') && !status.includes('resolved');
      }).length;
      const dueSoon = jiraIssues.filter((i) => {
        if (!i.fields.duedate) return false;
        const dueDate = new Date(i.fields.duedate);
        const sevenDaysFromNow = new Date();
        sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
        const status = i.fields.status.name.toLowerCase();
        const isOpen = !status.includes('done') && !status.includes('closed') && !status.includes('resolved');
        return dueDate <= sevenDaysFromNow && isOpen;
      }).length;
      const ppmInProgress = ppmCards.filter(
        (c) =>
          String(c.columnId || '').toLowerCase().includes('progress') &&
          !String(c.status || '').toUpperCase().includes('DONE') &&
          !String(c.status || '').toUpperCase().includes('ARCH'),
      ).length;
      const ppmOpen = ppmCards.filter(
        (c) =>
          !String(c.status || '').toUpperCase().includes('DONE') &&
          !String(c.status || '').toUpperCase().includes('ARCH'),
      ).length;
      const ppmDueSoon = ppmCards.filter((c) => {
        if (!c.dueDate) return false;
        const dueDate = new Date(c.dueDate);
        const sevenDaysFromNow = new Date();
        sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
        const isOpen = !String(c.status || '').toUpperCase().includes('DONE') && !String(c.status || '').toUpperCase().includes('ARCH');
        return dueDate <= sevenDaysFromNow && isOpen;
      }).length;
      return {
        activeProjects,
        inProgress: inProgress + ppmInProgress,
        open: open + ppmOpen,
        dueSoon: dueSoon + ppmDueSoon,
      };
    }

    const activeProjects = ppmProjects.length;
    const open = ppmCards.filter((c) => !String(c.status || '').toUpperCase().includes('DONE') && !String(c.status || '').toUpperCase().includes('ARCH')).length;
    const inProgress = ppmCards.filter((c) => String(c.columnId || '').toLowerCase().includes('progress') && !String(c.status || '').toUpperCase().includes('DONE')).length;
    const dueSoon = ppmCards.filter((c) => {
      if (!c.dueDate) return false;
      const dueDate = new Date(c.dueDate);
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      const isOpen = !String(c.status || '').toUpperCase().includes('DONE') && !String(c.status || '').toUpperCase().includes('ARCH');
      return dueDate <= sevenDaysFromNow && isOpen;
    }).length;
    return { activeProjects, inProgress, open, dueSoon };
  }, [canViewAll, jiraProjects, jiraIssues, ppmProjects, ppmCards]);

  const recentProjects = useMemo(() => {
    if (canViewAll) {
      const internal = ppmProjects
        .slice()
        .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))
        .slice(0, 3)
        .map((p) => ({ kind: 'ppm' as const, project: p }));
      const jira = jiraProjects.slice(0, 3).map((p) => ({ kind: 'jira' as const, project: p }));
      return [...internal, ...jira].slice(0, 6);
    }
    return ppmProjects
      .slice()
      .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))
      .slice(0, 6)
      .map((p) => ({ kind: 'ppm' as const, project: p }));
  }, [canViewAll, jiraProjects, ppmProjects]);

  const upcoming = useMemo(() => {
    const upcomingPpm = ppmCards
      .filter((c) => {
        if (!c.dueDate) return false;
        const dueDate = new Date(c.dueDate);
        const sevenDaysFromNow = new Date();
        sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
        const isOpen = !String(c.status || '').toUpperCase().includes('DONE') && !String(c.status || '').toUpperCase().includes('ARCH');
        return dueDate <= sevenDaysFromNow && isOpen;
      })
      .map((c) => ({ kind: 'ppm' as const, item: c, due: new Date(c.dueDate as any).getTime() }));

    const upcomingJira = jiraIssues
      .filter((i) => {
        if (!i.fields.duedate) return false;
        const dueDate = new Date(i.fields.duedate);
        const sevenDaysFromNow = new Date();
        sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
        const status = i.fields.status.name.toLowerCase();
        const isOpen = !status.includes('done') && !status.includes('closed') && !status.includes('resolved');
        return dueDate <= sevenDaysFromNow && isOpen;
      })
      .map((i) => ({ kind: 'jira' as const, item: i, due: new Date(i.fields.duedate as any).getTime() }));

    const merged = (canViewAll ? [...upcomingPpm, ...upcomingJira] : upcomingPpm)
      .slice()
      .sort((a, b) => a.due - b.due)
      .slice(0, 8);

    return merged;
  }, [canViewAll, jiraIssues, ppmCards]);

  const panelItems = useMemo(() => {
    if (!topPanel) return [];

    if (topPanel === 'activeProjects') {
      const ppm = (ppmProjects || []).map((p) => ({
        kind: 'ppm' as const,
        id: String(p.id),
        title: String(p.name || 'Untitled project'),
        subtitle: p.Portfolio?.name ? `Internal • ${p.Portfolio.name}` : 'Internal project',
        onClick: () => navigate(`/projects/internal/${p.id}`, { state: { from: location.pathname } }),
      }));
      const jira = (jiraProjects || []).map((p) => ({
        kind: 'jira' as const,
        id: String(p.id),
        title: String(p.name || 'Jira project'),
        subtitle: `Jira • ${p.key}`,
        onClick: () => navigate(`/projects/${p.key}`),
      }));
      const all = (canViewAll ? [...ppm, ...jira] : ppm)
        .slice()
        .sort((a, b) => a.title.localeCompare(b.title));
      return all;
    }

    if (topPanel === 'openWork') {
      const openPpm = (ppmCards || [])
        .filter((c) => !String(c.status || '').toUpperCase().includes('DONE') && !String(c.status || '').toUpperCase().includes('ARCH'))
        .map((c) => ({
          kind: 'ppm' as const,
          id: String(c.id),
          title: String(c.title || 'Work item'),
          subtitle: 'Internal work item',
          onClick: null as any, // no deep link yet
        }));

      const openJira = (jiraIssues || [])
        .filter((i) => {
          const status = i.fields.status.name.toLowerCase();
          return !status.includes('done') && !status.includes('closed') && !status.includes('resolved');
        })
        .map((i) => ({
          kind: 'jira' as const,
          id: String(i.id),
          title: `${i.key} — ${i.fields.summary}`,
          subtitle: `Jira • ${i.fields.status.name}`,
          onClick: null as any, // no deep link yet
        }));

      const all = (canViewAll ? [...openPpm, ...openJira] : openPpm).slice();
      return all;
    }

    // dueSoon
    return (upcoming || []).map((u: any) => {
      if (u.kind === 'jira') {
        const issue = u.item as JiraIssue;
        const due = issue.fields.duedate ? fmtDate(new Date(issue.fields.duedate)) : '—';
        return {
          kind: 'jira' as const,
          id: String(issue.id),
          title: `${issue.key} — ${issue.fields.summary}`,
          subtitle: `Due ${due} • ${issue.fields.status.name}`,
          onClick: null as any, // no deep link yet
        };
      }
      const card = u.item as KanbanCard;
      const due = card.dueDate ? fmtDate(new Date(card.dueDate as any)) : '—';
      return {
        kind: 'ppm' as const,
        id: String(card.id),
        title: String(card.title || 'Work item'),
        subtitle: `Due ${due} • Internal`,
        onClick: null as any, // no deep link yet
      };
    });
  }, [topPanel, canViewAll, ppmProjects, jiraProjects, ppmCards, jiraIssues, upcoming, navigate, location.pathname]);

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('done') || statusLower.includes('closed') || statusLower.includes('resolved')) return '#10B981';
    if (statusLower.includes('progress') || statusLower.includes('development')) return '#3B82F6';
    if (statusLower.includes('review') || statusLower.includes('testing')) return '#F59E0B';
    if (statusLower.includes('todo') || statusLower.includes('backlog')) return '#2563EB';
    return '#6B7280';
  };

  const viewModeLabel = canViewAll ? 'Admin view' : 'Your view';
  const viewModeDesc = canViewAll
    ? 'All projects + upcoming work items across Jira and internal delivery.'
    : 'Only projects you are a member of, with upcoming tasks.';

  return (
    <PageLoader loading={loading}>
      <Container maxWidth="xl" sx={{ pt: 1, pb: 3 }}>
      <PageHeader
        title="Workspace"
        subtitle={viewModeDesc}
        chips={[{ label: viewModeLabel }]}
        actions={
          <>
            <Button variant="outlined" onClick={() => navigate('/projects')}>
              Browse projects
            </Button>
            <Button variant="contained" onClick={() => fetchDashboardData()}>
              Refresh
            </Button>
          </>
        }
      />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            {loading ? (
              <Skeleton variant="rounded" height={122} sx={{ borderRadius: `${tokens.radius.lg}px` }} />
            ) : (
              <StatCard
                title="Active Projects"
                value={stats.activeProjects}
                subtitle={`${stats.inProgress} in progress`}
                accent={tokens.color.accent}
                delayMs={0}
                onClick={() => {
                  setShowAllPanel(false);
                  setTopPanel((p) => (p === 'activeProjects' ? null : 'activeProjects'));
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {loading ? (
              <Skeleton variant="rounded" height={122} sx={{ borderRadius: `${tokens.radius.lg}px` }} />
            ) : (
              <StatCard
                title="Open Work Items"
                value={stats.open}
                subtitle="Across visible projects"
                accent={tokens.color.info}
                delayMs={80}
                onClick={() => {
                  setShowAllPanel(false);
                  setTopPanel((p) => (p === 'openWork' ? null : 'openWork'));
                }}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {loading ? (
              <Skeleton variant="rounded" height={122} sx={{ borderRadius: `${tokens.radius.lg}px` }} />
            ) : (
              <StatCard
                title="Due Soon (7d)"
                value={stats.dueSoon}
                subtitle="Upcoming deadlines"
                accent={tokens.color.warning}
                delayMs={160}
                onClick={() => {
                  setShowAllPanel(false);
                  setTopPanel((p) => (p === 'dueSoon' ? null : 'dueSoon'));
                }}
              />
            )}
          </Grid>
        </Grid>

        <Collapse in={!!topPanel} unmountOnExit>
          <Card variant="outlined" sx={{ borderColor: tokens.color.border, mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                    {topPanel === 'activeProjects' ? 'Active projects' : topPanel === 'openWork' ? 'Open work items' : 'Due soon'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click again to close. (Jira item deep-links coming next.)
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" onClick={() => setShowAllPanel((s) => !s)}>
                    {showAllPanel ? 'Show less' : 'Show all'}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setTopPanel(null)}>
                    Close
                  </Button>
                </Stack>
              </Box>
              <Divider sx={{ my: 1.25 }} />

              {panelItems.length ? (
                <Stack spacing={1}>
                  {panelItems.slice(0, showAllPanel ? 1000 : 5).map((it: any) => (
                    <Card key={`${it.kind}_${it.id}`} variant="outlined" sx={{ borderColor: tokens.color.border }}>
                      <ButtonBase onClick={it.onClick} sx={{ width: '100%', textAlign: 'left' }} disabled={!it.onClick}>
                        <CardContent sx={{ width: '100%', py: 1.25 }}>
                          <Typography sx={{ fontWeight: 650 }} noWrap title={it.title}>
                            {it.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {it.subtitle}
                          </Typography>
                        </CardContent>
                      </ButtonBase>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Alert severity="info" variant="outlined">
                  Nothing to show.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Collapse>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Fade in timeout={450} style={{ transitionDelay: '120ms' }}>
              <Card variant="outlined" sx={{ borderColor: tokens.color.border }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                        Recent projects
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Jump back in where you left off.
                      </Typography>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => navigate('/projects')}>
                      View all
                    </Button>
                  </Box>
                  <Divider sx={{ my: 1.25 }} />

                  <Grid container spacing={1.25}>
                    {(loading ? new Array(6).fill(0) : recentProjects).slice(0, 6).map((rp: any, idx: number) => {
                      if (loading) {
                        return (
                          <Grid item xs={12} sm={6} key={`skeleton_${idx}`}>
                            <Skeleton variant="rounded" height={98} sx={{ borderRadius: `${tokens.radius.lg}px` }} />
                          </Grid>
                        );
                      }

                      const p = rp.project;
                      const kind = rp.kind;
                      const key = kind === 'jira' ? p.key : (p.name || 'Project');

                      return (
                        <Grid item xs={12} sm={6} key={`${kind}_${p.id}`}>
                          <Card
                            variant="outlined"
                            sx={{
                              height: '100%',
                              borderColor: tokens.color.border,
                              position: 'relative',
                              background: '#fff',
                              transition: 'border-color 120ms ease, background-color 120ms ease',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 3,
                                background: kind === 'jira' ? tokens.color.info : tokens.color.accent,
                              },
                              '&:hover': {
                                borderColor: tokens.color.borderStrong,
                                backgroundColor: tokens.color.surfaceMuted,
                              },
                            }}
                          >
                            <ButtonBase
                              onClick={() => {
                                if (kind === 'jira') navigate(`/projects/${p.key}`);
                                else navigate(`/projects/internal/${p.id}`, { state: { from: location.pathname } });
                              }}
                              sx={{ width: '100%', textAlign: 'left', height: '100%' }}
                            >
                              <CardContent sx={{ width: '100%' }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1, minWidth: 0 }}>
                                  <Avatar
                                    src={kind === 'jira' ? p.avatarUrls?.['48x48'] : undefined}
                                    sx={{
                                      width: 34,
                                      height: 34,
                                      borderRadius: 2,
                                      bgcolor: tokens.color.accentSoft,
                                      color: tokens.color.accentHover,
                                      fontWeight: 700,
                                      flexShrink: 0,
                                    }}
                                  >
                                    {kind === 'jira' ? 'J' : 'P'}
                                  </Avatar>
                                  <Typography sx={{ fontWeight: 650, flex: 1, minWidth: 0 }} noWrap>
                                    {p.name}
                                  </Typography>
                                </Box>
                                <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={key}
                                    sx={{ height: 22, fontWeight: 650, flexShrink: 0 }}
                                  />
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {kind === 'jira' ? 'Jira project' : 'Internal project'}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </ButtonBase>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {!loading && recentProjects.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No projects found.
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            </Fade>
          </Grid>

          <Grid item xs={12} md={6}>
            <Fade in timeout={450} style={{ transitionDelay: '180ms' }}>
              <Card variant="outlined" sx={{ borderColor: tokens.color.border }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                        Upcoming work
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Due in the next 7 days.
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1.25 }} />

                  <Stack spacing={1}>
                    {(loading ? new Array(6).fill(0) : upcoming).slice(0, 8).map((u: any, idx: number) => {
                      if (loading) {
                        return <Skeleton key={`work_skel_${idx}`} variant="rounded" height={78} sx={{ borderRadius: `${tokens.radius.lg}px` }} />;
                      }

                      const isJira = u.kind === 'jira';
                      const w = u.item;
                      const dueDate = isJira ? (w.fields.duedate ? new Date(w.fields.duedate) : null) : (w.dueDate ? new Date(w.dueDate) : null);
                      const overdue = dueDate && dueDate < new Date();
                      const statusLabel = isJira ? w.fields.status.name : String(w.status || 'TODO');
                      const statusColor = getStatusColor(statusLabel);

                      return (
                        <Card
                          key={`${u.kind}_${w.id}`}
                          variant="outlined"
                          sx={{
                            borderColor: tokens.color.border,
                            background: '#fff',
                            transition: 'border-color 120ms ease, background-color 120ms ease',
                            '&:hover': {
                              borderColor: tokens.color.borderStrong,
                              backgroundColor: tokens.color.surfaceMuted,
                            },
                          }}
                        >
                          <ButtonBase
                            onClick={() => {
                              if (isJira) {
                                const projectKey = w.key.split('-')[0];
                                navigate(`/projects/${projectKey}`);
                              } else {
                                const pid = ppmProjects.find((p) => p.id === w.boardId || String(w.boardId || '').includes(p.id))?.id;
                                if (pid) navigate(`/projects/internal/${pid}`, { state: { from: location.pathname } });
                              }
                            }}
                            sx={{ width: '100%', textAlign: 'left' }}
                          >
                            <CardContent sx={{ width: '100%' }}>
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Box sx={{ minWidth: 0 }}>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
                                    <Chip size="small" label={isJira ? w.key : (w.name || 'Project')} sx={{ height: 22, fontWeight: 650, background: tokens.color.accentSoft, color: tokens.color.accentHover }} />
                                    <Chip
                                      size="small"
                                      label={statusLabel}
                                      sx={{
                                        height: 22,
                                        fontWeight: 650,
                                        background: `${statusColor}1A`,
                                        color: statusColor,
                                      }}
                                    />
                                    <Chip size="small" label={isJira ? 'Jira' : 'Internal'} sx={{ height: 22 }} />
                                  </Stack>
                                  <Typography sx={{ fontWeight: 650 }} noWrap>
                                    {isJira ? w.fields.summary : w.title}
                                  </Typography>
                                </Box>
                                {dueDate ? (
                                  <Chip
                                    size="small"
                                    label={`${overdue ? 'Overdue' : 'Due'} ${fmtDate(dueDate)}`}
                                    sx={{
                                      height: 24,
                                      fontWeight: 650,
                                      background: overdue ? tokens.color.dangerSoft : tokens.color.warningSoft,
                                      color: overdue ? tokens.color.danger : tokens.color.warning,
                                      flexShrink: 0,
                                    }}
                                  />
                                ) : null}
                              </Stack>
                            </CardContent>
                          </ButtonBase>
                        </Card>
                      );
                    })}

                    {!loading && upcoming.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No upcoming work items.
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            </Fade>
          </Grid>
        </Grid>
      </Container>
    </PageLoader>
  );
};

export default Dashboard;
